import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import restaurantStaffService from '../services/restaurantStaffService'
import { RestaurantStaffInvitation, ROLE_LABELS } from '../types/restaurantStaff'
import { useTranslation } from '../hooks/use-i18n'

const StaffInvitations: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [invitations, setInvitations] = useState<RestaurantStaffInvitation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [actingId, setActingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await restaurantStaffService.getMyInvitations()
      setInvitations(data)
      restaurantStaffService.markAllRead().catch(() => undefined)
    } catch (err: any) {
      console.error('Failed to load invitations:', err)
      toast.error(t('toast.invitationsLoadFailed'))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    load()
  }, [load])

  const respond = async (
    invitation: RestaurantStaffInvitation,
    status: 'accepted' | 'rejected',
  ) => {
    try {
      setActingId(invitation._id)
      await restaurantStaffService.respond(invitation._id, status)
      if (status === 'accepted') {
        toast.success(t('toast.joinedRestaurantStaff', { restaurantName: invitation.restaurant?.name || t('common.fallbackRestaurant') }))
        if (invitation.restaurant) {
          navigate(`/restaurant/${invitation.restaurant._id}`)
          return
        }
      } else {
        toast.success(t('toast.staff.invitationDeclined'))
      }
      await load()
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.invitationUpdateFailed'))
    } finally {
      setActingId(null)
    }
  }

  const pending = invitations.filter((i) => i.status === 'pending')
  const history = invitations.filter((i) => i.status !== 'pending')

  return (
    <div className="min-h-screen">
      <main className="max-w-3xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="mb-6 sm:mb-8">
          <Link
            to="/account"
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            ← {t('nav.account')}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{t('staff.invitations.title')}</h1>
          <p className="text-white/60 mt-2">
            {t('staff.invitations.subtitle')}
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500"></div>
          </div>
        ) : invitations.length === 0 ? (
          <div className="bg-[#2a2a2a] border border-white/10 rounded-3xl p-10 text-center">
            <div className="text-4xl mb-3">📭</div>
            <h2 className="text-xl font-bold text-white mb-2">{t('staff.invitations.empty.title')}</h2>
            <p className="text-white/60">{t('staff.invitations.empty.body')}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {pending.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-white/60 uppercase tracking-wide mb-3">
                  {t('staff.invitations.pending')}
                </h2>
                <div className="space-y-3">
                  {pending.map((inv) => (
                    <div
                      key={inv._id}
                      className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-5"
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-white font-semibold text-lg">
                            {inv.restaurant?.name || t('common.fallbackRestaurant')}
                          </p>
                          <p className="text-white/60 text-sm mt-1">
                            {t('staff.invitations.invitedAs', { sender: inv.sender.name, role: ROLE_LABELS[inv.role] })}
                          </p>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">
                          {t('staff.invitations.pending')}
                        </span>
                      </div>
                      <div className="flex gap-3 mt-4">
                        <button
                          type="button"
                          onClick={() => respond(inv, 'accepted')}
                          disabled={actingId === inv._id}
                          className="flex-1 px-4 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl text-sm font-semibold transition-all duration-300 disabled:opacity-50"
                        >
                          {actingId === inv._id ? t('staff.invitations.working') : t('staff.invitations.accept')}
                        </button>
                        <button
                          type="button"
                          onClick={() => respond(inv, 'rejected')}
                          disabled={actingId === inv._id}
                          className="flex-1 px-4 py-2.5 border-2 border-white/30 text-white rounded-xl text-sm font-semibold hover:bg-white/10 transition-all duration-300 disabled:opacity-50"
                        >
                          {t('staff.invitations.decline')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {history.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-white/60 uppercase tracking-wide mb-3">
                  {t('staff.invitations.history')}
                </h2>
                <div className="space-y-3">
                  {history.map((inv) => (
                    <div
                      key={inv._id}
                      className="bg-[#2a2a2a]/60 border border-white/5 rounded-2xl p-5 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-white/80 font-medium truncate">
                          {inv.restaurant?.name || t('common.fallbackRestaurant')}
                        </p>
                        <p className="text-white/50 text-sm">{ROLE_LABELS[inv.role]}</p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                          inv.status === 'accepted'
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            : 'bg-red-500/20 text-red-300 border border-red-500/30'
                        }`}
                      >
                        {inv.status}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  )
}

export default StaffInvitations
